import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {fetchGoogleTagManagerByOwnerIdsApi} from "~/api/googleTagManager";
import {getMortgageProviderIdFromAppOrSelected} from "~/store/selectors/basicInfo";
import {getWhitelabelId} from "~/store/selectors/whitelabel";
import {removeGtm, injectGtm} from "./useInjectGtm.util";

export default function useInjectGtm() {
  const [financialGtmIds, setFinancialGtmIds] = useState<string[]>([]);
  const [whitelabelGtmIds, setWhitelabelGtmIds] = useState<string[]>([]);

  const previousFinancialGtmIdsRef = useRef<string[]>([]);

  const financialInstitutionId = useSelector(getMortgageProviderIdFromAppOrSelected);
  const whitelabelId = useSelector(getWhitelabelId);

  const fetchGtmIds = async () => {
    const ids = [whitelabelId, financialInstitutionId].filter((id): id is string => !!id);
    const {data, error} = await fetchGoogleTagManagerByOwnerIdsApi(ids);

    if (error) {
      console.error("Unable to load gtm ids", error);
      return;
    }

    const wl: string[] = [];
    const fi: string[] = [];

    data?.forEach(({googleTagId, ownerId}) => {
      if (ownerId === whitelabelId) wl.push(googleTagId);
      if (ownerId === financialInstitutionId) fi.push(googleTagId);
    });

    setWhitelabelGtmIds(wl);
    setFinancialGtmIds(fi);
  };

  useEffect(() => {
    if (whitelabelId || financialInstitutionId) {
      fetchGtmIds();
    }
  }, [whitelabelId, financialInstitutionId]);

  useEffect(() => {
    const prev = previousFinancialGtmIdsRef.current;
    const next = financialGtmIds;
    const toRemove = prev.filter((id) => !next.includes(id));
    const toAdd = [...whitelabelGtmIds, ...next];

    // Sequentially remove and inject gtm
    (async () => {
      try {
        // Wait for all removals to complete
        if (toRemove.length > 0) {
          await removeGtm();
        }

        // Once removed, inject the new gtm tag(s)
        toAdd.forEach(injectGtm);

      } catch (error) {
        console.error("Error updating gtm tags:", error);
      }
    })();

    previousFinancialGtmIdsRef.current = financialGtmIds;
  }, [financialGtmIds]);
}
